export type ColorModePreference = 'light' | 'dark' | 'system';

export const COLOR_MODE_PREFERENCE_STORAGE_KEY = 'bower-color-mode-preference';

/**
 * Module-level store for the user's three-state colour-mode preference.
 *
 * Two readers need the same value: the Preferences tab (to highlight the
 * chosen button) and `ColorModePreferenceSync` at the app root (to follow the
 * OS scheme while the preference is "System"). Keeping it in a hook's local
 * state meant only whichever component happened to be mounted knew about it,
 * and the root sync could not react to a change made in Settings. Read through
 * `useSyncExternalStore` so both stay in step.
 *
 * `localStorage` is the source of truth; the snapshot reads it directly. It is
 * a primitive string, so `useSyncExternalStore` sees a stable value between
 * writes, and tests can arrange state by writing the key.
 *
 * Every storage access is guarded. `readStoredColorModePreference` is the
 * `getSnapshot` of a `useSyncExternalStore` in `ColorModePreferenceSync`,
 * which wraps the whole tree at the app root, so a throw there is not a wrong
 * theme but a blank app. `localStorage` throws outright in storage-blocked
 * contexts (a private window with site data blocked, a sandboxed iframe) and
 * `setItem` throws on a zero quota. Chakra's own `createLocalStorageManager`
 * wraps both calls for the same reason, but it only ever protected Chakra's
 * key, never this one. When a write fails the choice is kept in memory so it
 * still applies for the rest of the session.
 */

const listeners = new Set<() => void>();

/** The last choice that could not be persisted; read in preference to storage. */
let unpersisted: ColorModePreference | null = null;

function isPreference(value: unknown): value is ColorModePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

export function readStoredColorModePreference(): ColorModePreference {
  if (typeof window === 'undefined') return 'system';
  if (unpersisted !== null) return unpersisted;
  try {
    const saved = window.localStorage.getItem(COLOR_MODE_PREFERENCE_STORAGE_KEY);
    return isPreference(saved) ? saved : 'system';
  } catch {
    return 'system';
  }
}

export function subscribeToColorModePreference(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setStoredColorModePreference(next: ColorModePreference): void {
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(COLOR_MODE_PREFERENCE_STORAGE_KEY, next);
      unpersisted = null;
    } catch {
      unpersisted = next;
    }
  }
  listeners.forEach((listener) => listener());
}

/**
 * Test seam: forget an unpersisted choice so cases do not leak into each other.
 *
 * @internal
 */
export function resetUnpersistedColorModePreference(): void {
  unpersisted = null;
}

/** Server snapshot for `useSyncExternalStore`: no window, so the default. */
export function getServerColorModePreference(): ColorModePreference {
  return 'system';
}
